import { RpcEngine } from '../core/rpc.js'
import { METHODS, PUSH } from '../core/events.js'
import type {
  ChatErrorPayload,
  ChatSendTask,
  ChatStreamPayload,
  ChatStreamStartPayload,
  Message,
  SendOptions,
  MessagesModule,
  GenerateRawMessage,
  GenerateRawOptions,
  Unsubscribe,
} from '../types/index.js'

const DEFAULT_AI_RPC_TIMEOUT_MS = 60_000

export class ChatModule {
  readonly messages: MessagesModule

  constructor(private rpc: RpcEngine) {
    this.messages = {
      list:   (params) => rpc.call<Message[]>(METHODS.CHAT_GET_HISTORY, [params]),
      getAll: ()       => rpc.call<Message[]>(METHODS.CHAT_GET_HISTORY, [undefined]),
      delete: (id)     => rpc.call(METHODS.CHAT_MESSAGE_DELETE, [id]),
      edit:   (id, content)   => rpc.call<Message>(METHODS.CHAT_MESSAGE_EDIT, [id, content]),
      // Compatibility placeholder. Current platform hosts may reject this call.
      swipe:  (id, direction) => rpc.call<Message>(METHODS.CHAT_MESSAGE_SWIPE, [id, direction]),
    }
  }

  private static genRequestId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `chat-${crypto.randomUUID()}`
    }
    return `chat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  }

  /**
   * Send a message to the character.
   *
   * The platform processes the request through its normal chat flow.
   * `onData` fires for each streaming chunk; `onComplete` fires when the
   * stream finishes; `onError` fires if the generation fails.
   */
  send(message: string, options: SendOptions = {}): void {
    this.sendStream(message, options).done.catch(() => {})
  }

  stop():       Promise<void> { return this.rpc.call(METHODS.CHAT_STOP) }
  regenerate(): Promise<void> { return this.rpc.call(METHODS.CHAT_REGENERATE) }
  continue():   Promise<void> { return this.rpc.call(METHODS.CHAT_CONTINUE) }
  getInput():   Promise<string> { return this.rpc.call<string>(METHODS.CHAT_GET_INPUT) }
  setInput(text: string): Promise<void> { return this.rpc.post(METHODS.CHAT_SET_INPUT, [text]) }

  /** Set the active greeting index before the first user message is sent */
  setGreetingIndex(index: number): Promise<number> {
    return this.rpc.call<number>(METHODS.CHAT_SET_GREETING_INDEX, [index])
  }

  /** Get the current active greeting index */
  getGreetingIndex(): Promise<number> {
    return this.rpc.call<number>(METHODS.CHAT_GET_GREETING_INDEX)
  }

  /** @deprecated Use setGreetingIndex(index) */
  setFirstMesIndex(index: number): Promise<number> {
    return this.rpc.call<number>(METHODS.CHAT_SET_FIRST_MES, [index])
  }

  sendStream(message: string, options: SendOptions = {}): ChatSendTask {
    const requestId = options.requestId ?? ChatModule.genRequestId()
    let accumulated = ''
    let settled = false
    let cleanup = () => {}

    const matchesRequest = (payload: { requestId?: string }) => !payload.requestId || payload.requestId === requestId

    const done = new Promise<Message>((resolve, reject) => {
      const timeoutMs = options.timeoutMs ?? DEFAULT_AI_RPC_TIMEOUT_MS
      let timeout: ReturnType<typeof setTimeout> | undefined

      const armTimeout = () => {
        if (timeout) clearTimeout(timeout)
        timeout = setTimeout(() => {
          if (settled) return
          settled = true
          cleanup()
          const error = new Error(`[CharxSdk] chat.send timeout: ${requestId}`)
          options.onError?.(error)
          reject(error)
        }, timeoutMs)
      }

      const finish = (handler: () => void) => {
        if (settled) return
        settled = true
        if (timeout) clearTimeout(timeout)
        cleanup()
        handler()
      }

      const startUnsub = this.rpc.push(PUSH.CHAT_STARTED, (data) => {
        const payload = data as ChatStreamStartPayload
        if (!matchesRequest(payload)) return
        armTimeout()
        options.onStart?.(payload)
      })

      const streamUnsub = this.rpc.push(PUSH.CHAT_STREAM_CHUNK, (data) => {
        const payload = data as ChatStreamPayload
        if (!matchesRequest(payload)) return
        armTimeout()
        if (!payload.done) {
          accumulated += payload.chunk
          options.onData?.(payload.chunk)
          return
        }
        const content = payload.fullContent ?? accumulated
        const messageId = payload.messageId ?? payload.assistantMessageId ?? requestId
        const messageResult: Message = {
          id: String(messageId),
          role: 'assistant',
          content,
          createdAt: Date.now(),
        }
        options.onComplete?.(messageResult)
        finish(() => resolve(messageResult))
      })

      const errorUnsub = this.rpc.push(PUSH.CHAT_ERROR, (data) => {
        const payload = data as ChatErrorPayload
        if (!matchesRequest(payload)) return
        armTimeout()
        const error = new Error(payload.message || payload.error || 'AI 生成失败')
        options.onError?.(error)
        finish(() => reject(error))
      })

      cleanup = () => {
        startUnsub()
        streamUnsub()
        errorUnsub()
      }

      armTimeout()
    })

    this.rpc.post(METHODS.CHAT_SEND, [
      message,
      {
        requestId,
        model: options.model,
        temperature: options.temperature,
        injectedSystemPrompts: options.injectedSystemPrompts,
      },
    ])

    return {
      requestId,
      done,
      stop: () => this.rpc.call(METHODS.CHAT_STOP, [requestId]),
    }
  }

  /**
   * Inject a system prompt into the next user-facing generation call.
   * Equivalent to SillyTavern injectPrompts().
   * The injected prompt is cleared automatically after the next chat.send().
   */
  injectPrompt(content: string): Promise<void> {
    return this.rpc.post(METHODS.CHAT_INJECT_PROMPT, [content])
  }

  /**
   * Insert a system message into the conversation without triggering AI generation.
   * Equivalent to SillyTavern createChatMessages() with role=system.
   */
  insertSystemMessage(content: string): Promise<Message> {
    return this.rpc.call<Message>(METHODS.CHAT_INSERT_SYSTEM_MESSAGE, [content])
  }

  /** Get the current conversation ID */
  getConversationId(): Promise<number> {
    return this.rpc.call<number>(METHODS.CHAT_GET_CONVERSATION_ID)
  }

  /**
   * Subscribe to conversation switch events.
   * Fires when the user navigates to a different conversation.
   * Equivalent to SillyTavern CHAT_CHANGED event.
   */
  onChatChanged(callback: (payload: { conversationId: number }) => void): Unsubscribe {
    return this.rpc.push(PUSH.CHAT_CHANGED, callback as (data: unknown) => void)
  }

  /**
   * Subscribe to message-updated events.
   * Fires when an assistant message is fully saved (stream complete) or updated.
   * Equivalent to SillyTavern MESSAGE_UPDATED event.
   */
  onMessageUpdated(callback: (message: Message) => void): Unsubscribe {
    return this.rpc.push(PUSH.MESSAGE_UPDATED, callback as (data: unknown) => void)
  }

  /**
   * Subscribe to message-edited events.
   * Fires when a message is edited via sdk.chat.messages.edit().
   * Equivalent to SillyTavern MESSAGE_EDITED event.
   */
  onMessageEdited(callback: (message: Message) => void): Unsubscribe {
    return this.rpc.push(PUSH.MESSAGE_EDITED, callback as (data: unknown) => void)
  }

  /**
   * Silent background LLM generation — equivalent to SillyTavern generateRaw().
   * Calls the AI model without adding any messages to the chat history.
   * When includeCharacterContext is true (default), the character's system prompt
   * and worldbook are included in the request context.
   */
  generateRaw(messages: GenerateRawMessage[], options: GenerateRawOptions = {}): Promise<string> {
    return this.rpc.call<string>(METHODS.CHAT_GENERATE_RAW, [messages, options], options.timeoutMs ?? DEFAULT_AI_RPC_TIMEOUT_MS)
  }

  /**
   * Subscribe to message-deleted events.
   * Fires when a message is deleted via sdk.chat.messages.delete().
   * Equivalent to SillyTavern MESSAGE_DELETED event.
   */
  onMessageDeleted(callback: (message: Message) => void): Unsubscribe {
    return this.rpc.push(PUSH.MESSAGE_DELETED, callback as (data: unknown) => void)
  }
}
